import { Box, Button, Modal, Tree } from "fds";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DeptInfo, TreeNode, User } from "../../types/adminDept";

interface Props {
  moveUserInfo: User[];
  moveDeptInfo: DeptInfo;
  /** adminDeptStore.internalTree.subTree */
  internalTreeSubTree: TreeNode[];
  companyName: string;
  ROOT_DEPT_CODE: string;
  /** 이동 불가 대상 (부서 이동 시 자기 자신) */
  targetId: string;
  onClose: () => void;
  onApply: (targetCode: string, targetName: string) => Promise<void>;
  searchTree: (value: string) => void;
}

const MoveModal: React.FC<Props> = ({
  moveUserInfo,
  moveDeptInfo,
  internalTreeSubTree,
  companyName,
  ROOT_DEPT_CODE,
  targetId,
  onClose,
  onApply,
  searchTree,
}) => {
  const { t } = useTranslation();
  const [moveTargetCode, setMoveTargetCode] = useState("");
  const [moveTargetName, setMoveTargetName] = useState("");
  const [expandValues, setExpandValues] = useState([ROOT_DEPT_CODE]);

  const isMovingUsers = moveUserInfo.length > 0;

  function findDeptNodes(trees: TreeNode[], excludeId: string): TreeNode[] {
    function excludeSubTree(node: TreeNode): TreeNode | null {
      if (node.value === excludeId) {
        return { ...node, showChildren: false, hasChildren: false, subTree: [] };
      }
      const filteredChildren = node.subTree
        .map(excludeSubTree)
        .filter((child): child is TreeNode => child !== null);
      if (node.type === "dept") {
        return { ...node, subTree: filteredChildren };
      }
      return null;
    }

    return trees
      .map(excludeSubTree)
      .filter((n): n is TreeNode => n !== null);
  }

  const handleClose = () => {
    setMoveTargetCode("");
    setMoveTargetName("");
    setExpandValues([ROOT_DEPT_CODE]);
    onClose();
  };

  const handleApply = async () => {
    await onApply(moveTargetCode, moveTargetName);
    setMoveTargetCode("");
    setMoveTargetName("");
    setExpandValues([ROOT_DEPT_CODE]);
  };

  return (
    <Modal
      width={490}
      height={600}
      title={isMovingUsers ? t("admin.dept.transferUserDept") : t("admin.dept.transferDept")}
      open
      onClose={handleClose}
      footer={
        <Box justifyContent="flex-end">
          <Button
            label={t("admin.common.button.cancel")}
            type="secondary"
            btnStyle="fill"
            onClick={handleClose}
          />
          <Button
            label={t("admin.common.button.apply")}
            type="primary"
            btnStyle="fill"
            state={moveTargetCode === "" ? "disable" : "default"}
            onClick={handleApply}
          />
        </Box>
      }
      body={
        <Box height={426}>
          <Tree
            width="100%"
            treeData={{
              columnText: companyName,
              subTree: findDeptNodes(
                [...internalTreeSubTree],
                isMovingUsers ? "" : targetId
              ),
              showChildren: true,
              hasChildren: true,
              value: ROOT_DEPT_CODE,
              type: "company",
            }}
            expandAction={(value: string) => {
              if (expandValues.includes(value)) {
                setExpandValues(expandValues.filter((item) => item !== value));
              } else {
                setExpandValues([...expandValues, value]);
                searchTree(value);
              }
            }}
            depth={0}
            checkType="radio"
            treeType="eco"
            checkValues={[moveTargetCode]}
            checkAction={(value: any) => {
              setMoveTargetCode(value.value);
              setMoveTargetName(value.columnText);
            }}
            selectNode={() => {}}
            expandValues={expandValues}
            expandSearchValues={[moveTargetCode]}
            disableValues={isMovingUsers ? [] : [targetId]}
          />
        </Box>
      }
    />
  );
};

export default MoveModal;
